import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  updateProcess,
  type ProcessDetailDto,
  type WriteProcessEdgeDto,
  type WriteProcessNodeDto,
} from '@api/processes';
import {
  processDetailQueryKey,
} from '../hooks/useProcessDetail';

/**
 * Auto-save state machine — surfaced in the topbar via
 * {@link SaveStatusIndicator}.
 *
 *  - `idle`    — no edits since the last save OR local edits within
 *                the debounce window. Blurring "no edits" and "pre-
 *                debounce" into one state stops the chip flapping on
 *                every drag tick.
 *  - `saving`  — a PUT is on the wire (the debounce just fired).
 *  - `saved`   — a save just succeeded; transient (auto-fades to idle).
 *  - `error`   — last save failed (transport, 500, validation).
 *  - `drift`   — 409 DRIFT_DETECTED; caller must show a Refresh
 *                affordance (we never auto-merge — that would silently
 *                drop the edits saved from another tab). Drafts are
 *                per-user so the "other writer" is always the same user
 *                in another tab/window, never a teammate.
 */
export type AutoSaveStatus =
  | 'idle'
  | 'saving'
  | 'saved'
  | 'error'
  | 'drift';

export interface AutoSaveState {
  readonly status: AutoSaveStatus;
  /** ISO timestamp of the last successful save (server-side updatedAt). */
  readonly lastSavedAt: string | null;
  /** Human-readable error message when status is `error` or `drift`. */
  readonly errorMessage: string | null;
}

export interface UseProcessAutoSaveArgs {
  /** Process id under edit. Auto-save is disabled when undefined. */
  processId: string | undefined;
  /** Current canvas nodes (React Flow → write DTOs done by the caller). */
  nodes: readonly WriteProcessNodeDto[];
  /** Current canvas edges. */
  edges: readonly WriteProcessEdgeDto[];
  /** The latest detail payload — drives expectedUpdatedAt and metadata. */
  detail: ProcessDetailDto | undefined;
  /**
   * Whether auto-save should run. Pass `false` to suspend the loop entirely
   * (e.g. published process, no permission to edit, route teardown).
   */
  enabled: boolean;
  /** Debounce window in ms. Plan default: 1500. */
  debounceMs?: number;
}

/**
 * Per-flush overrides for fields that don't live on the canvas (nodes /
 * edges) but still need to ride along on the next PUT body. The mutation
 * reads them in preference to {@link detailRef.current}, sidestepping the
 * "I just optimistically wrote the React Query cache but the
 * useEffect([detail]) sync hasn't committed yet" race that would
 * otherwise send the previous value to the server.
 */
export interface AutoSaveFlushOverrides {
  readonly name?: string;
}

export interface UseProcessAutoSaveReturn extends AutoSaveState {
  /**
   * Force an immediate save (bypassing the debounce). Returns the fresh
   * server-side `ProcessDetailDto`, or rejects on transport / drift /
   * validation failure. Useful for the Publish button and the inline
   * rename flow (which passes its trimmed name via {@link
   * AutoSaveFlushOverrides} so the PUT body doesn't race the cache
   * propagation).
   */
  flush: (
    overrides?: AutoSaveFlushOverrides,
  ) => Promise<ProcessDetailDto | undefined>;
  /**
   * Last-writer-wins escape hatch for a 409 DRIFT_DETECTED. Re-stamps
   * the PUT's `expectedUpdatedAt` to the server's current `updatedAt`
   * (captured from the original 409 response, falling back to the React
   * Query cache) and retries the PUT with the user's local canvas
   * state, overwriting whatever the other writer saved.
   *
   * Only valid while {@code status === 'drift'}; a no-op otherwise.
   * Surfaces the in-flight state via {@link isForceSaving} so the
   * blocking drift modal can show a spinner without flipping the
   * autosave indicator off the "Out of sync" pill (we deliberately
   * keep {@code status} at `drift` during the force-save so the modal
   * stays mounted even if a second 409 races us).
   *
   * Resolves on success or rejects with the underlying error so the
   * caller can surface a snackbar on non-drift failures.
   */
  forceSave: () => Promise<ProcessDetailDto | undefined>;
  /** True while {@link forceSave}'s PUT is in flight. */
  readonly isForceSaving: boolean;
  /**
   * Arm the drift-recovery effect so the next bump of
   * {@code detail.updatedAt} is allowed to dismiss the drift state.
   *
   * The drift effect deliberately ignores passive
   * {@code queryClient.setQueryData} / refetch updates -- otherwise an
   * unrelated invalidate (run-session events, publish, "clear statuses",
   * etc.) would silently flip the hook out of {@code drift}, the
   * blocker modal would un-mount, and the next debounced save would
   * happily overwrite the version the user never saw. The two modal
   * handlers MUST call this just before the action that will bump
   * {@code detail.updatedAt} (Refresh → invalidate, Save anyway →
   * forceSave). The effect single-shots the flag on consume; the
   * mutation's onSuccess / onError also reset it so a force-save that
   * settles without going through the effect doesn't leak the arming
   * into a later passive refetch.
   */
  confirmDriftRecovery: () => void;
  /**
   * Disarm a previously-armed drift recovery. The handler MUST call
   * this on the failure path of the action it armed (e.g. the refresh
   * invalidate rejected, the force-save threw before the mutation
   * ran) so a subsequent passive cache update can't sneak the
   * stale arming through.
   */
  cancelDriftRecovery: () => void;
  /**
   * Accept a save someone else made on this user's behalf — today only the
   * process assistant, writing through the control plane's MCP tools while
   * the user watches this canvas.
   *
   * Such a save bumps the server's `updatedAt` behind this hook's back, so
   * without adopting it the next PUT would carry a token the server has
   * already moved past and 409 as "changed elsewhere" — a lie, since the
   * change was made here, for this user. Adopting rebaselines
   * `expectedUpdatedAt` from the next payload and re-snapshots the canvas
   * that payload seeds, so no redundant PUT follows either.
   *
   * Call it BEFORE triggering the refetch that carries the new version, and
   * only when the canvas is clean: with unsaved local edits the conflict is
   * real and belongs to the drift modal.
   */
  adoptExternalSave: () => void;
  /**
   * Disarm {@link adoptExternalSave}. The caller MUST do this if the refetch
   * it armed never delivers a new version, or the arming survives to adopt
   * whatever unrelated update lands next — which would rebaseline
   * `expectedUpdatedAt` onto a version the user never saw.
   */
  cancelExternalSaveAdoption: () => void;
}

interface ApiErrorBody {
  readonly error?: string;
  readonly message?: string;
  readonly details?: { readonly currentUpdatedAt?: string };
}

const DEFAULT_DEBOUNCE_MS = 1500;
const SAVED_FADE_MS = 1500;

const isDriftError = (err: unknown): err is AxiosError<ApiErrorBody> => {
  const ax = err as AxiosError<ApiErrorBody> | undefined;
  if (!ax || typeof ax !== 'object') return false;
  return ax.isAxiosError === true && ax.response?.status === 409 &&
    ax.response.data?.error === 'DRIFT_DETECTED';
};

/**
 * Auto-save loop for the process editor.
 *
 * Wraps a debounced PUT against `/processes/:id` keyed off
 * `(nodes, edges)`. The serialised payload is shallow-compared against the
 * last sent payload so node-position drags or no-op renders don't trigger
 * a redundant save.
 *
 * On 409 DRIFT_DETECTED we transition to `drift` and stop saving until the
 * caller refreshes the cache (the `SaveStatusIndicator` snackbar exposes a
 * Refresh action that fires `queryClient.invalidateQueries(detailKey)`).
 *
 * On unmount the in-flight mutation is left to settle (React Query will
 * still write its result into the cache); the state machine just stops
 * surfacing transitions because the component is gone.
 */
export function useProcessAutoSave(
  args: UseProcessAutoSaveArgs,
): UseProcessAutoSaveReturn {
  const {
    processId,
    nodes,
    edges,
    detail,
    enabled,
    debounceMs = DEFAULT_DEBOUNCE_MS,
  } = args;
  const queryClient = useQueryClient();

  const [state, setState] = useState<AutoSaveState>({
    status: 'idle',
    lastSavedAt: detail?.updatedAt ?? null,
    errorMessage: null,
  });

  // Refs that the debounce + mutation closures need a fresh read of without
  // re-creating themselves on every nodes / edges tick. State is mirrored
  // into a ref so the mutation's onError handler can always read the
  // *latest* lastSavedAt (otherwise it would close over a stale value
  // from the render at which the mutation was created).
  const detailRef = useRef<ProcessDetailDto | undefined>(detail);
  const nodesRef = useRef<readonly WriteProcessNodeDto[]>(nodes);
  const edgesRef = useRef<readonly WriteProcessEdgeDto[]>(edges);
  const stateRef = useRef<AutoSaveState>(state);
  const lastSentRef = useRef<string>('');
  const debounceHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedFadeHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const driftedRef = useRef<boolean>(false);
  // Server-authoritative {@code updatedAt} captured from the 409
  // response's {@code details.currentUpdatedAt}. {@link forceSave}
  // stamps this onto the next PUT's {@code expectedUpdatedAt} so the
  // backend's drift check accepts our last-writer-wins overwrite.
  // We pin to the 409 payload (not {@code detailRef.current.updatedAt})
  // because the React Query cache can move under us via background
  // refetches the user never observed -- the 409 details are the only
  // value the user explicitly "saw" was on the server.
  const driftServerUpdatedAtRef = useRef<string | null>(null);
  // Per-mutation override for {@code expectedUpdatedAt}. Set by
  // {@link forceSave} just before {@code mutation.mutateAsync()} so the
  // mutationFn picks it up in preference to {@code stateRef.current.lastSavedAt},
  // then cleared in onSuccess / onError. Kept out of the public
  // {@link AutoSaveFlushOverrides} surface because it's an internal
  // signal between forceSave and the mutation, not a flush concern.
  const forcedExpectedUpdatedAtRef = useRef<string | null>(null);
  const mountedRef = useRef<boolean>(true);
  // Whether a force-save PUT is currently in flight. Drives the drift
  // modal's loading state without tampering with the autosave status
  // machine (which stays at 'drift' across the in-flight window so the
  // modal stays mounted even if a second 409 races us).
  const [isForceSaving, setIsForceSaving] = useState<boolean>(false);
  // Set by the drift-recovery and adopt-external-save effects to signal
  // "rebaseline lastSentRef from the next nodes/edges value we see".
  // Neither can baseline inline because the Discard path (page calls
  // {@code graph.setGraph} after the refetch) leaves the canvas
  // one render behind the {@code detail.updatedAt} bump that fires
  // the recovery. Baselining inline against the stale canvas would
  // make the next debounce see a diff (the post-discard canvas) and
  // fire a "save the server state right back to itself" no-op PUT,
  // which would in turn bump the server's updatedAt and 409 every
  // other tab that's open to this process. Deferring to the next
  // nodes/edges tick lands the correct baseline before any debounce.
  const pendingBaselineRef = useRef<boolean>(false);
  // Drift recovery is now opt-in. The recovery effect (below) refuses
  // to clear {@code driftedRef} on a {@code detail.updatedAt} bump
  // unless this flag is set, which the modal handlers do just before
  // their action. This protects against background invalidates from
  // unrelated code paths -- {@code useProcessRunSession} alone fires
  // {@code invalidateQueries(processDetailQueryKey)} five times during
  // a run, and any of those landing while the user is staring at the
  // drift modal would otherwise silently dismiss it (AIB-6976 regression).
  const driftRecoveryConfirmedRef = useRef<boolean>(false);
  // Armed by {@link adoptExternalSave}: the next {@code detail.updatedAt}
  // we see is a save made for this user (by the process assistant), not a
  // competing writer, so it becomes our baseline instead of a 409.
  const pendingAdoptRef = useRef<boolean>(false);
  // AIB-7058: at most one PUT to /processes/:id on the wire at a
  // time. Every save (debounce, flush, forceSave) chains behind this
  // promise. A second PUT issued before the first response carries a
  // stale {@code expectedUpdatedAt} and the backend correctly 409s
  // it, surfacing the "saved in another tab" modal in a single-tab
  // session.
  const inFlightRef =
    useRef<Promise<ProcessDetailDto | undefined> | null>(null);
  // Generation that owns {@link inFlightRef}. Set synchronously
  // alongside {@code inFlightRef.current} in {@link runSerialisedMutation};
  // {@link flush} uses it to decide whether to propagate the in-flight
  // rejection. Without this, a save started under a previous processId
  // that the navigation reset deliberately retained (so its response
  // can settle into the previous process's cache) would, on failure,
  // reject {@code flush()} for the new process and block Run / Publish
  // / Rename. Distinct from {@link inflightGenerationRef}: that one is
  // set inside {@code mutationFn} (after a microtask) and is for
  // stale-settlement detection in onSuccess/onError.
  const inFlightOwnerGenerationRef = useRef<number | null>(null);
  // Coalesces N edits during one in-flight save into a single
  // follow-up PUT. Set by the debounce timer when it would have
  // fired but {@link inFlightRef} was non-null; drained by the
  // mutation's onSuccess/onError via {@link flushPendingDebounceRef}.
  const pendingDebouncedFlushRef = useRef<boolean>(false);
  // Indirection to let onSuccess/onError -- whose closures predate
  // {@code flushPendingDebounce} -- invoke the latest version.
  const flushPendingDebounceRef = useRef<() => void>(() => undefined);
  // What this in-flight PUT actually sent. Baseline source for
  // {@code lastSentRef} in onSuccess: using {@code nodesRef.current}
  // there would falsely attribute mid-flight edits to the in-flight
  // save, and the coalesced follow-up would drop them as "nothing
  // more to send".
  const inflightSerialisedRef = useRef<string | null>(null);
  // Generation token tied to the active processId. Bumped on
  // processId reset so a save in flight to a previous processId is
  // recognised as stale when it settles and skips all state, cache,
  // and pending-flush mutations -- otherwise the new process would
  // get corrupted (cache write, lastSavedAt bump, phantom flush)
  // because React Query's onSuccess closure resolves to the CURRENT
  // render's processId.
  const generationRef = useRef<number>(0);
  // Generation captured at {@code mutationFn} time; compared in
  // onSuccess/onError to detect a stale settlement.
  const inflightGenerationRef = useRef<number | null>(null);

  useEffect(() => {
    detailRef.current = detail;
  }, [detail]);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Reset the autosave state machine when the user navigates to a
  // different process. Crucially this does NOT depend on
  // {@code detail.updatedAt} -- if it did, every successful save (which
  // writes the fresh detail into the React Query cache) would re-fire
  // this effect and slam the state back to 'idle', erasing the 'saved'
  // pulse and -- worse -- erasing any subsequent 'error' transition that
  // happened in the same render cycle. Drift recovery is handled by a
  // separate effect below that fires on updatedAt change but only when
  // we're actually in a drifted state.
  useEffect(() => {
    lastSentRef.current = '';
    driftedRef.current = false;
    driftServerUpdatedAtRef.current = null;
    forcedExpectedUpdatedAtRef.current = null;
    pendingBaselineRef.current = false;
    driftRecoveryConfirmedRef.current = false;
    pendingAdoptRef.current = false;
    pendingDebouncedFlushRef.current = false;
    inflightSerialisedRef.current = null;
    // Bump the generation so any save in flight to the previous
    // processId is recognised as stale on settle. We deliberately
    // do NOT null {@code inFlightRef}: React Query still needs to
    // write its response into the previous process's cache, and the
    // {@code .finally} observer in {@code runSerialisedMutation}
    // nulls the slot itself when the promise resolves.
    generationRef.current += 1;
    if (savedFadeHandleRef.current) {
      clearTimeout(savedFadeHandleRef.current);
      savedFadeHandleRef.current = null;
    }
    setState({
      status: 'idle',
      lastSavedAt: detail?.updatedAt ?? null,
      errorMessage: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only reset on processId change.
  }, [processId]);

  // Track whether we are currently mounted so {@link safeSetState} can no-op
  // after a real unmount. CRITICAL: the setup body MUST re-assert
  // {@code mountedRef.current = true}. React's Strict Mode (dev only) runs
  // every effect setup → cleanup → setup again on the very first commit;
  // without the re-assertion the strict-mode cleanup leaves {@code mountedRef}
  // stuck at {@code false} for the entire (real) lifetime of the hook,
  // silently dropping every subsequent state transition (the seed
  // {@code lastSavedAt}, every {@code pending}/{@code saving}/{@code error}
  // transition…) so the indicator pill never appears and never updates.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceHandleRef.current) {
        clearTimeout(debounceHandleRef.current);
      }
      if (savedFadeHandleRef.current) {
        clearTimeout(savedFadeHandleRef.current);
      }
    };
  }, []);

  const safeSetState = useCallback((next: AutoSaveState) => {
    if (!mountedRef.current) return;
    // If we're transitioning AWAY from the transient 'saved' state,
    // cancel its pending fade-to-idle timer. Without this the timer
    // outlives the transition and later overwrites whatever state we're
    // now in (e.g. 'error' from a follow-up failed save) back to 'idle'
    // -- which combined with a non-zero validation count would silently
    // mask a real save failure as "Saved · N issues".
    if (next.status !== 'saved' && savedFadeHandleRef.current) {
      clearTimeout(savedFadeHandleRef.current);
      savedFadeHandleRef.current = null;
    }
    stateRef.current = next;
    setState(next);
  }, []);

  // Drift recovery: when the user explicitly chose Refresh or Save-
  // anyway on the drift modal, that handler armed
  // {@code driftRecoveryConfirmedRef} via {@link confirmDriftRecovery},
  // then triggered the action that bumps {@code detail.updatedAt}
  // (Refresh → {@code queryClient.invalidateQueries(...)} → refetch;
  // Save-anyway → mutation success → {@code setQueryData(...)}). This
  // effect picks up that bump, single-shots the confirmation flag,
  // clears {@code driftedRef}, and arms the deferred-baseline effect
  // so the next nodes/edges value lands into {@code lastSentRef}.
  //
  // The {@code driftRecoveryConfirmedRef} gate is load-bearing: there
  // are at least 8 unrelated code paths that invalidate the detail
  // query (run-session events x5, publish, clear-statuses, run errors)
  // and ANY of those landing while the user is staring at the drift
  // modal would otherwise silently dismiss it -- the canvas would
  // un-lock, the next debounce would PUT, and the version the user
  // never saw would be overwritten (AIB-6976 silent-edit-loss).
  useEffect(() => {
    if (!detail?.updatedAt) return;
    if (!driftedRef.current) return;
    if (!driftRecoveryConfirmedRef.current) return;
    driftRecoveryConfirmedRef.current = false;
    driftedRef.current = false;
    pendingBaselineRef.current = true;
    safeSetState({
      status: 'idle',
      lastSavedAt: detail.updatedAt,
      errorMessage: null,
    });
  }, [detail?.updatedAt, safeSetState]);

  // Adopt an assistant save (see {@link adoptExternalSave}). Same shape as
  // drift recovery -- take the new server version as our own baseline and
  // defer the lastSent snapshot to the reseeded canvas -- but without the
  // modal, because nothing was in conflict.
  //
  // An already-drifted hook is left alone: the user is looking at the
  // blocker and only their answer to it may move the baseline.
  useEffect(() => {
    if (!pendingAdoptRef.current) return;
    if (!detail?.updatedAt) return;
    if (driftedRef.current) {
      pendingAdoptRef.current = false;
      return;
    }
    if (detail.updatedAt === stateRef.current.lastSavedAt) return;
    pendingAdoptRef.current = false;
    pendingBaselineRef.current = true;
    safeSetState({
      status: 'idle',
      lastSavedAt: detail.updatedAt,
      errorMessage: null,
    });
  }, [detail?.updatedAt, safeSetState]);

  // Deferred lastSent baseline. Runs on the next nodes/edges tick
  // after drift recovery so the page's post-refetch setGraph call has
  // a chance to land the discarded-back-to-server canvas before we
  // snapshot it -- see {@code pendingBaselineRef} for the
  // longer rationale. The {@code lastSentRef.current === ''} guard
  // dodges the seed effect's first tick so we don't accidentally
  // re-baseline before the initial detail has loaded.
  useEffect(() => {
    if (!pendingBaselineRef.current) return;
    if (lastSentRef.current === '') return;
    pendingBaselineRef.current = false;
    lastSentRef.current = serialise(nodes, edges);
  }, [nodes, edges]);

  const mutation = useMutation<
    ProcessDetailDto | undefined,
    unknown,
    AutoSaveFlushOverrides | undefined
  >({
    mutationFn: async (
      overrides?: AutoSaveFlushOverrides,
    ): Promise<ProcessDetailDto | undefined> => {
      const id = processId;
      const d = detailRef.current;
      // CRITICAL: source {@code expectedUpdatedAt} from the hook's own
      // {@code state.lastSavedAt}, NOT from {@code detailRef.current.updatedAt}.
      //
      // The semantics of {@code expectedUpdatedAt} are "the version of the
      // graph this user committed to editing". That value must only ever
      // advance via paths the user actually saw:
      //   1. their own save succeeded (onSuccess),
      //   2. they explicitly clicked Refresh after a 409 (drift recovery).
      //
      // {@code detailRef.current.updatedAt} mirrors the React Query cache,
      // which is moved by background refetches the user never observed --
      // most importantly the default {@code refetchOnWindowFocus: true}.
      // Two-tab scenario: tab A saves, tab B regains focus, React Query
      // silently refetches and bumps the cache's updatedAt to tab A's new
      // server value, then tab B's autosave fires with that fresh
      // updatedAt -- backend sees no drift and tab A's edits get clobbered.
      // Pinning expectedUpdatedAt to {@code stateRef.current.lastSavedAt}
      // makes the drift check survive any background cache movement.
      //
      // The {@code forcedExpectedUpdatedAtRef} branch is the force-save
      // escape hatch: when the user explicitly chose "Save anyway" on
      // the drift modal we re-stamp the PUT with the server's current
      // {@code updatedAt} (captured from the original 409) so the
      // backend's drift check accepts our overwrite. Cleared in
      // onSuccess / onError so it can't accidentally outlive its
      // intended single PUT.
      const expectedUpdatedAt =
        forcedExpectedUpdatedAtRef.current ?? stateRef.current.lastSavedAt;
      if (!id || !d || !expectedUpdatedAt) return undefined;
      // Capture the snapshot we're about to PUT (for onSuccess
      // baselining) and the active generation (for stale-settlement
      // detection) BEFORE the await -- onSuccess fires after an
      // arbitrary network delay during which both could move.
      const snapshot = serialise(nodesRef.current, edgesRef.current);
      inflightSerialisedRef.current = snapshot;
      inflightGenerationRef.current = generationRef.current;
      // Per-flush overrides win over the cached detail. The inline
      // rename flow passes {@code name} here because its
      // setQueryData(...) write hasn't propagated through
      // useEffect([detail]) into detailRef.current yet -- without this
      // the PUT body would still carry the pre-rename name and the
      // server would silently keep the old one.
      return updateProcess(id, {
        name: overrides?.name ?? d.name,
        description: d.description,
        maxIterationsPerNode: d.maxIterationsPerNode,
        expectedUpdatedAt,
        nodes: nodesRef.current,
        edges: edgesRef.current,
      });
    },
    onSuccess: (fresh) => {
      // Stale-settlement guard: skip all side effects if this save
      // started under a previous processId.
      if (inflightGenerationRef.current !== generationRef.current) {
        inflightGenerationRef.current = null;
        inflightSerialisedRef.current = null;
        return;
      }
      // Always clear the single-shot force-save override -- whether the
      // PUT was a debounce save or a force-save, it has now settled and
      // the next mutation must fall back to the state machine.
      forcedExpectedUpdatedAtRef.current = null;
      inflightGenerationRef.current = null;
      // Clear the captured server time as well: any subsequent 409 will
      // come with its own fresh {@code currentUpdatedAt} payload.
      driftServerUpdatedAtRef.current = null;
      // Disarm any pending drift-recovery confirmation. The force-save
      // path arms the flag (via the modal handler) but its onSuccess
      // clears {@code driftedRef} inline -- the recovery effect then
      // early-returns without consuming the flag, leaving it set. Left
      // armed, the NEXT 409 + passive refetch combo would silently
      // dismiss the resulting drift modal. Clearing here keeps the
      // flag a single-shot per user action.
      driftRecoveryConfirmedRef.current = false;
      if (!fresh || !processId) {
        inflightSerialisedRef.current = null;
        return;
      }
      // Baseline against what THIS PUT sent (not the current canvas)
      // so mid-flight edits stay dirty and the coalesced follow-up
      // doesn't drop them. Fallback is defensive.
      lastSentRef.current =
        inflightSerialisedRef.current ??
        serialise(nodesRef.current, edgesRef.current);
      inflightSerialisedRef.current = null;
      queryClient.setQueryData(processDetailQueryKey(processId), fresh);
      // A successful save (force or otherwise) supersedes any prior
      // drift -- clear the flag so the recovery effect doesn't fire
      // when the cache update propagates back into {@code detail} and
      // bumps {@code detail.updatedAt}. Without this, force-save would
      // double-baseline lastSentRef via the recovery effect AFTER
      // onSuccess already did it, which is benign but wasteful.
      driftedRef.current = false;
      safeSetState({
        status: 'saved',
        lastSavedAt: fresh.updatedAt,
        errorMessage: null,
      });
      if (savedFadeHandleRef.current) {
        clearTimeout(savedFadeHandleRef.current);
      }
      savedFadeHandleRef.current = setTimeout(() => {
        safeSetState({
          status: 'idle',
          lastSavedAt: fresh.updatedAt,
          errorMessage: null,
        });
      }, SAVED_FADE_MS);
      // Coalesced follow-up for any debounce trigger that deferred
      // while we were on the wire (AIB-7058).
      flushPendingDebounceRef.current();
    },
    onError: (err: unknown) => {
      // Stale-settlement guard (symmetrical to onSuccess).
      if (inflightGenerationRef.current !== generationRef.current) {
        inflightGenerationRef.current = null;
        inflightSerialisedRef.current = null;
        return;
      }
      // Clear the single-shot force-save override so a retry uses the
      // freshly-captured {@code currentUpdatedAt} (below) instead of
      // re-stamping the now-stale value we just sent.
      forcedExpectedUpdatedAtRef.current = null;
      inflightGenerationRef.current = null;
      // Nothing persisted, so drop the snapshot without baselining
      // {@code lastSentRef} -- the user's edits must stay dirty.
      inflightSerialisedRef.current = null;
      // Disarm drift-recovery confirmation. If the user clicked Save-
      // anyway and we hit another 409 (race with a third writer) we
      // want the modal to stay up AND a subsequent unrelated
      // invalidate to NOT auto-dismiss it. Re-arming requires a fresh
      // user action on the modal.
      driftRecoveryConfirmedRef.current = false;
      if (isDriftError(err)) {
        driftedRef.current = true;
        const fresh = err.response?.data?.details?.currentUpdatedAt ?? null;
        // Pin the server's authoritative {@code updatedAt} so the next
        // {@link forceSave} can overwrite cleanly. If the response
        // didn't carry it (older backend?) keep whatever we had so a
        // previous capture still lets force-save try.
        if (fresh) driftServerUpdatedAtRef.current = fresh;
        safeSetState({
          status: 'drift',
          lastSavedAt: stateRef.current.lastSavedAt,
          errorMessage:
            'You saved changes in another tab. Refresh to load them — your local changes will be lost.' +
            (fresh ? ` (server time ${fresh})` : ''),
        });
        return;
      }
      const ax = err as AxiosError<ApiErrorBody> | Error;
      const message =
        ('response' in ax && ax.response?.data?.message) ||
        (ax instanceof Error ? ax.message : null) ||
        'Failed to save.';
      safeSetState({
        status: 'error',
        lastSavedAt: stateRef.current.lastSavedAt,
        errorMessage: message,
      });
      // Coalesced follow-up for any deferred edit. Transient
      // failures get a retry; persistent ones just re-surface
      // 'error', no worse than today.
      flushPendingDebounceRef.current();
    },
  });

  // Serialise every PUT through a chained-promise queue (AIB-7058).
  // Chaining via {@code .then} -- rather than gating on some
  // {@code isPending} flag -- means serialisation holds even for
  // callers firing in the same microtask, since each captures
  // {@code previous} into its closure. {@code previous.catch} keeps
  // a prior failure from poisoning the chain; the {@code .finally}
  // nulls {@code inFlightRef} only if we still own the slot so a
  // post-settle re-entry (the coalesced flush) doesn't clear it
  // prematurely.
  const runSerialisedMutation = useCallback(
    (
      overrides?: AutoSaveFlushOverrides,
    ): Promise<ProcessDetailDto | undefined> => {
      const previous = inFlightRef.current;
      const next: Promise<ProcessDetailDto | undefined> = previous
        ? previous
            .catch(() => undefined)
            .then(() => mutation.mutateAsync(overrides))
        : mutation.mutateAsync(overrides);
      inFlightRef.current = next;
      inFlightOwnerGenerationRef.current = generationRef.current;
      next
        .finally(() => {
          if (inFlightRef.current === next) {
            inFlightRef.current = null;
            inFlightOwnerGenerationRef.current = null;
          }
        })
        // Swallow rejection from this observer chain; callers handle
        // {@code next} themselves.
        .catch(() => undefined);
      return next;
    },
    [mutation],
  );

  // Drains {@code pendingDebouncedFlushRef} from the mutation's
  // onSuccess / onError. Reads the LATEST canvas snapshot so N
  // mid-flight edits coalesce into a single follow-up PUT.
  const flushPendingDebounce = useCallback(() => {
    if (!pendingDebouncedFlushRef.current) return;
    if (driftedRef.current || !mountedRef.current) {
      pendingDebouncedFlushRef.current = false;
      return;
    }
    const next = serialise(nodesRef.current, edgesRef.current);
    if (next === lastSentRef.current) {
      pendingDebouncedFlushRef.current = false;
      return;
    }
    pendingDebouncedFlushRef.current = false;
    safeSetState({
      status: 'saving',
      lastSavedAt: stateRef.current.lastSavedAt,
      errorMessage: null,
    });
    runSerialisedMutation(undefined).catch(() => undefined);
  }, [runSerialisedMutation, safeSetState]);

  // Wire the ref so the mutation's onSuccess / onError -- whose
  // closures predate {@code flushPendingDebounce} -- always see the
  // latest version.
  useEffect(() => {
    flushPendingDebounceRef.current = flushPendingDebounce;
  }, [flushPendingDebounce]);

  // Debounced auto-save trigger. Re-armed on every (nodes, edges)
  // tick — the body MUST stay just {@code clearTimeout + setTimeout},
  // any work done here would dominate the drag CPU profile (this
  // effect runs once per mouse-move). Serialise + setState happen
  // inside the timer callback once the user pauses.
  //
  // The {@code lastSentRef.current === ''} guard skips the very first
  // tick before the seed-on-first-detail effect below has populated
  // the baseline — without it we'd fire a spurious save with the
  // freshly-loaded graph as "edit".
  useEffect(() => {
    if (!enabled || !processId || !detail) return;
    if (driftedRef.current) return;
    if (lastSentRef.current === '') return;
    if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
    debounceHandleRef.current = setTimeout(() => {
      // Compare against the latest snapshot at the moment the timer
      // fires (NOT the snapshot from the render that armed the timer)
      // so a drag that returns the node to its original position
      // doesn't fire a no-op save.
      const next = serialise(nodesRef.current, edgesRef.current);
      if (next === lastSentRef.current) return;
      // Defer to the coalesced post-settle flush if a PUT is on the
      // wire -- firing now would race a stale token and 409 (AIB-7058).
      if (inFlightRef.current) {
        pendingDebouncedFlushRef.current = true;
        return;
      }
      safeSetState({
        status: 'saving',
        lastSavedAt: stateRef.current.lastSavedAt,
        errorMessage: null,
      });
      runSerialisedMutation(undefined).catch(() => undefined);
    }, debounceMs);
    // Cleanup needed for the disable/unmount case: a pending timer
    // would otherwise fire one final save against a now-disabled editor.
    return () => {
      if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: re-arm the loop only on the tracked deps; mutation identity is stable in practice and stateRef sidesteps stale-closure reads.
  }, [enabled, processId, nodes, edges, debounceMs, detail, safeSetState]);

  // Seed the lastSent baseline AND the lastSavedAt timestamp when we first
  // see a detail payload. Two reasons:
  //   1. Without seeding lastSentRef, the first canvas tick would compare
  //      against an empty string and immediately fire a redundant save.
  //   2. Without seeding lastSavedAt, the indicator's pre-first-edit guard
  //      ({@code status === 'idle' && !lastSavedAt → return null}) would
  //      hide the pill entirely until the first successful save -- the
  //      user opens an existing process and sees no save status at all.
  //      Seeding from {@code detail.updatedAt} makes "All changes saved"
  //      render immediately, which is accurate (the loaded graph IS the
  //      persisted graph).
  useEffect(() => {
    if (!detail) return;
    if (lastSentRef.current === '') {
      lastSentRef.current = serialise(nodes, edges);
    }
    if (stateRef.current.status === 'idle' && stateRef.current.lastSavedAt === null) {
      safeSetState({
        status: 'idle',
        lastSavedAt: detail.updatedAt,
        errorMessage: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-seed on first detail / process change.
  }, [detail?.processId]);

  const flush = useCallback(
    async (
      overrides?: AutoSaveFlushOverrides,
    ): Promise<ProcessDetailDto | undefined> => {
      if (!enabled || !processId || !detail) return undefined;
      if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
      // Drain any in-flight save AND any coalesced follow-ups its
      // onSuccess synchronously queues. Awaiting only the original
      // in-flight save was racy for Publish: A's onSuccess fires
      // {@link flushPendingDebounceRef} which can chain a save B
      // behind A; flush() would resume after A, observe a
      // canvas-matches-lastSent no-op (or take its own runSerialisedMutation
      // path) and return A's updatedAt while B was still on the
      // wire. Publish then POSTed against a stale expectedUpdatedAt
      // and the backend 409'd. Looping drains the chain: each
      // settling save can enqueue another, and runSerialisedMutation's
      // .finally nulls the slot only when nothing chained behind it.
      //
      // CRITICAL: propagate the in-flight rejection ONLY for saves
      // started under the current generation. Callers wire flush()
      // into save-before-run / save-before-publish gates and expect
      // a thrown error to mean "stop, the save failed". Silently
      // catching here lets a drift / validation / network failure
      // look like success to the caller. BUT a save retained from a
      // previous processId (the navigation reset deliberately leaves
      // {@code inFlightRef} populated so its response can settle into
      // the previous process's cache) belongs to a process the user
      // has already left -- its rejection must not block Run /
      // Publish / Rename on the new process. The ownership check is
      // re-evaluated each iteration so a mid-loop processId change
      // bails out cleanly. {@code runSerialisedMutation} still chains
      // behind it for AIB-7058, swallowing the rejection in its own
      // internal {@code .catch(() => undefined)}.
      let inFlightResult: ProcessDetailDto | undefined;
      while (
        inFlightRef.current &&
        inFlightOwnerGenerationRef.current === generationRef.current
      ) {
        inFlightResult = await inFlightRef.current;
      }
      // Skip the PUT when the graph matches what was last sent AND
      // the caller didn't supply per-flush overrides. Without this,
      // callers that flush as a pre-condition (e.g. "save before Run")
      // would bump updatedAt on every Run -- inflating drift noise
      // for other tabs and the audit log, and wasting a round-trip.
      const next = serialise(nodesRef.current, edgesRef.current);
      if (next === lastSentRef.current && !overrides) {
        return inFlightResult ?? detailRef.current;
      }
      safeSetState({
        status: 'saving',
        lastSavedAt: stateRef.current.lastSavedAt,
        errorMessage: null,
      });
      return runSerialisedMutation(overrides);
    },
    [enabled, processId, detail, runSerialisedMutation, safeSetState],
  );

  const forceSave = useCallback(
    async (): Promise<ProcessDetailDto | undefined> => {
      // Guards: must be enabled, have a process + detail, AND actually
      // be in a drift state. Calling forceSave outside drift is a
      // programming error -- silently ignore rather than firing a save
      // with a synthetic expectedUpdatedAt.
      if (!enabled || !processId || !detail) return undefined;
      if (stateRef.current.status !== 'drift') return undefined;
      // Prefer the server's authoritative value captured from the 409
      // payload. Fall back to detailRef.current.updatedAt when the
      // backend response somehow omitted it (older backend, hand-rolled
      // proxy) -- the cache may be stale but it's the best we have, and
      // a second 409 will refresh the captured value on retry.
      const serverUpdatedAt =
        driftServerUpdatedAtRef.current ?? detailRef.current?.updatedAt;
      if (!serverUpdatedAt) return undefined;
      // Cancel any pending debounce -- forceSave is an explicit user
      // action that supersedes the debounced loop.
      if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
      // Stash the override for mutationFn to pick up. Status stays at
      // 'drift' deliberately so the blocking modal stays mounted even
      // if the PUT races a second writer and 409s again.
      forcedExpectedUpdatedAtRef.current = serverUpdatedAt;
      setIsForceSaving(true);
      try {
        // Route through the serialised queue rather than relying on
        // the drift-state gate to keep the debounce loop quiet.
        return await runSerialisedMutation(undefined);
      } finally {
        // Guarded against the strict-mode-cleanup pattern where
        // setState after unmount warns. mountedRef tracks our real
        // lifecycle (see the mount-tracker effect's comment for why
        // it must be re-asserted on setup).
        if (mountedRef.current) setIsForceSaving(false);
      }
    },
    [enabled, processId, detail, runSerialisedMutation],
  );

  const confirmDriftRecovery = useCallback(() => {
    driftRecoveryConfirmedRef.current = true;
  }, []);

  const cancelDriftRecovery = useCallback(() => {
    driftRecoveryConfirmedRef.current = false;
  }, []);

  const adoptExternalSave = useCallback(() => {
    // Drop any armed debounce: whatever it would send predates the save
    // we're adopting, and the reseed about to land supersedes it.
    if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
    pendingAdoptRef.current = true;
  }, []);

  const cancelExternalSaveAdoption = useCallback(() => {
    pendingAdoptRef.current = false;
  }, []);

  return {
    ...state,
    flush,
    forceSave,
    isForceSaving,
    confirmDriftRecovery,
    cancelDriftRecovery,
    adoptExternalSave,
    cancelExternalSaveAdoption,
  };
}

const serialise = (
  nodes: readonly WriteProcessNodeDto[],
  edges: readonly WriteProcessEdgeDto[],
): string => JSON.stringify({ nodes, edges });
